/*
* @Description: im 实例
* @Author: FengQi
* @Email: zhaolong.dzl@alibaba-inc.com
* @Date:   2018-11-07 14:38:51
* @Last Modified by:   FengQi
* @Last Modified time: 2019-03-07 11:26:07
* @Copyright(c) 2018 Taobao.com
*/

import EventEmitter from 'events';
import Connect from './connect';
import requestMtop from './mtop';
import MD5 from 'md5';
import { isEmptyJson, parseParam, encode, uuid, pick, queryToString, genUrl, getURL, Jsonp, Cors, request } from './util';

const DEFAULT_USER_ID = MD5(navigator.userAgent + new Date().setHours(0, 0, 0, 0));

export default class IM extends EventEmitter {

  constructor(options) {

    if (!options || !options.appName || !options.tenantId || !options.clientType || !options.userType) {
      throw new Error('Error params: more details -- https://yuque.antfin-inc.com/docs/share/926eb41b-1c65-4b53-a02c-6567b0f928af');
    }

    super();

    this.options = options;

    this.env = options.env || 'daily';

    this.debug = !!options.debug;

    // 集团后端鉴权成功与否
    this.authenticated = false;

    const paramKeys = ['clientType', // 端类型， 1: PC，2: H5(moblie theme), 3: iOS，4: Android
    'userType', // 用户类型，1：会员，2：小二，4：钉钉小蜜机器人，5：店小蜜机器人
    'appName', 'sid', 'buzUserId', 'userId', 'userName', 'xcode', 'tt', 'tenantId', 'departmentId', 'skillGroupId', 'outSessionId', 'sourceId', 'timestamp', 'token'];

    // 所有接口公用数据
    this._params = pick(options, paramKeys);
    // 字体问题
    this._params._input_charset = 'utf-8';

    // 标一下全局数据
    this.sid = this._params.sid;
    this.buzUserId = this._params.buzUserId;
    this.userId = this._params.userId;
    this.userName = this._params.userName;
    this.xcode = this._params.xcode;
    this.tt = this._params.tt;
    this.tenantId = this._params.tenantId;
    this.departmentId = this._params.departmentId;
    this.skillGroupId = this._params.skillGroupId;
    this.outSessionId = this._params.outSessionId;
    this.sourceId = this._params.sourceId;
    this.token = this._params.token;

    /**
     * 根据 env 指定接口域名。
     * 如果 env 是 daily，域名则用 daily.taobao.net；
     * 如果指定了 apiHost，则用 apiHost 的值；
     * @type {object}
     */
    const host = this.env === 'daily' ? 'daily.taobao.net' : options.apiHost || 'taobao.com';
    const connectedDomain = options.isHotline ? 'connected-hotline' : 'connected';
    const prepre = this.env === 'pre' ? 'pre-' : '';
    this.domain = {
      method: options.method || 'JSONP', // 接口请求方式：JSONP、CORS
      harbor: `//${prepre}webim.${host}`,
      connected: `//${prepre}${connectedDomain}.${host}`,
      timestamp: -1

      // 长轮询实例
    };this.con = null;

    // OSS 上传相关信息
    this.OSSInfo = null;
  }

  _log(...msg) {
    if (this.debug) {
      console.log(...msg);
    }
  }

  _mergeParams(data = {}) {
    return Object.assign({}, this._params, data);
  }

  /**
   * 发送 mtop 请求
   *
   */
  _mtop(apiName, data, opts) {
    return requestMtop(apiName, data, Object.assign({
      env: this.env
    }, opts));
  }

  _request(name, params = {}, options = {}) {
    return request(name, this.domain, this._mergeParams(params), options);
  }

  /**
   * 更新共有参数
   * @param  {String/Object} key   添加数据的key，或者是对象
   * @param  {String} value        当key时string时，其为对应的value
   */
  updateParams(key, value) {
    if (typeof key == 'object') {
      parseParam(this._params, key);
    } else if (typeof key == 'string') {
      parseParam(this._params, { key, value });
    }

    this._updateParams();
  }

  /**
   * 添加公共参数
   * @param  {String/Object} key   添加数据的key，或者是对象
   * @param  {String} value        当key时string时，其为对应的value
   */
  addParams(key, value) {
    if (typeof key == 'object') {
      for (let o in key) {
        this._params[o] = key[o];
      }
    } else if (typeof key == 'string') {
      this._params[key] = value;
    }

    this._updateParams();
  }

  /**
   * 重新更新共有参数 _params
   */
  _updateParams() {
    // 标一下全局数据
    this.sid = this._params.sid;
    this.buzUserId = this._params.buzUserId;
    this.userId = this._params.userId;
    this.userName = this._params.userName;
    this.xcode = this._params.xcode;
    this.tt = this._params.tt;
    this.tenantId = this._params.tenantId;
    this.departmentId = this._params.departmentId;
    this.skillGroupId = this._params.skillGroupId;
    this.outSessionId = this._params.outSessionId;
    this.sourceId = this._params.sourceId;
    this.token = this._params.token;

    // 把参数合并到options中
    Object.assign(this.options, this._params);

    // 同步长连接的参数
    if (this.con) {
      this.con._params = this._params;
    }
  }

  /**
   * 重置参数
   */
  _resetParams() {
    this.options = null;

    this.env = '';

    this.debug = false;
    this.authenticated = false;

    // 全局数据
    this.sid = null;
    this.buzUserId = null;
    this.userId = null;
    this.userName = null;
    this.xcode = null;
    this.tt = null;
    this.tenantId = null;
    this.departmentId = null;
    this.skillGroupId = null;
    this.outSessionId = null;
    this.sourceId = null;
    this.token = null;

    this._params = null;

    this.con = null;
    this.OSSInfo = null;
  }

  /**
   * 获取共有参数 _params
   * @return {Object} SDK的共有参数
   */
  getParams() {
    return this._params;
  }

  /**
   * 用户鉴权方法
   * @param  {Object} spec 包含用户鉴权信息
   * 不传时表示游客登录；传时需要传入使用部门的鉴权后信息
   */
  login(spec = {}) {

    // 如果不为空，说明是在外部登录成功后出入相关数据，这时需要检查数据完整性
    if (isEmptyJson(spec) || !spec.buzUserId || !spec.userId || !spec.userName || !spec.xcode || !spec.tt) {
      throw new Error('Error params: more details -- https://yuque.antfin-inc.com/docs/share/926eb41b-1c65-4b53-a02c-6567b0f928af');
    }

    // 必传 frontEndUserId
    Object.assign(spec, {
      frontEndUserId: spec && spec.frontEndUserId || DEFAULT_USER_ID
    });

    return this._request('login', spec).then(data => {
      // 如果后端没有鉴权成功，之后的接口都不让调用
      if (!data || !data.tt || !data.userId || !data.xcode) {
        throw new Error('Error authentication!');
      }

      this.authenticated = true;

      // 添加时间戳
      data.timestamp = data.tt;
      // 合并接口参数
      this.updateParams(data);
      return data;
    }, e => {
      return e;
    });
  }

  /**
   * 判断对应技能组是否在工作时间
   * @return {Promise}
   */
  isWorking(spec = {}) {

    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    if (isEmptyJson(spec) || !spec.skillGroupId) {
      throw new Error('Error params: more details -- https://yuque.antfin-inc.com/docs/share/926eb41b-1c65-4b53-a02c-6567b0f928af');
    }

    return this._request('isWorking', spec).then(data => {
      return data;
    }, e => {
      return e;
    });
  }

  /**
   * 派发人工小二接口
   * @param  {Object} spec   请求参数
   * @return {Promise}       派发小二Promise对象
   */
  dispatch(spec = {}) {

    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    // 会员在线超时接入点
    if (!spec.sourceId) {
      throw new Error('Error params: need sourceId, more details -- https://yuque.antfin-inc.com/docs/share/926eb41b-1c65-4b53-a02c-6567b0f928af');
    }

    // 把传入的数据合并到options和params
    Object.assign(this.options, spec);
    this.updateParams(spec);

    // pick请求接口参数
    const paramKeys = ['appName', 'stationType', 'sourceId', 'userId', 'userName', 'tenantId', 'outSessionId', 'servicerId', 'skillGroupId', 'cusBuzInfo'];
    const params = pick(this.options, paramKeys);

    // 如果没有指定小二 也没有指定技能组
    if (!params.servicerId && !params.skillGroupId) {
      throw new Error('Error params: ' + 'servicerId || skillGroupId is necessary!');
    }

    // 默认 xspace
    params.stationType = params.stationType || 'xspace';

    return this._request('dispatch', params).then(data => {
      this._startSession(data);

      return data;
    }, e => {
      return e;
    });
  }

  /**
   * 手动尝试恢复会话
   *
   * @param  {Object} params 传入数据
   */
  resume(spec = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    // 会员在线超时接入点
    spec.sourceId = spec.sourceId || this.sourceId;
    if (!spec.sourceId) {
      throw new Error('Error params: need sourceId, more details -- https://yuque.antfin-inc.com/docs/share/926eb41b-1c65-4b53-a02c-6567b0f928af');
    }

    // 把传入的数据合并到options和params
    Object.assign(this.options, spec);
    this.updateParams(spec);

    // pick请求接口参数
    const paramKeys = ['appName', 'userId', 'userName'];
    const params = pick(this.options, paramKeys);

    if (!params.userName) {
      throw new Error('Error params: need userName, more details -- https://yuque.antfin-inc.com/docs/share/926eb41b-1c65-4b53-a02c-6567b0f928af');
    }

    return this._request('resume', Object.assign({}, params, {
      supportNewFunction: this.con && this.con._isReadStatusOn
    })).then(data => {
      // 更新公共参数
      // this.addParams(data);

      // 开始轮询
      if (data.isInQueue || data.isServicing || data.isContinue) {
        this._startSession(data);
      }

      // 然后返回出去
      return data;
    }, err => {
      throw err;
    });
  }

  /**
   * 开始长轮询
   * @param  {Object} data 分配好小二后返回的数据（可能是resume 或 dispatch的返回数据）
   */
  _startSession(data = {}) {
    this.updateParams(data);

    // 分配好后开始连通小二
    this.con = Connect(Object.assign({}, this.options, { params: this._params, domain: this.domain }));
    this._message();
  }

  /**
   * 启动消息监听
   */
  _message() {
    if (!this.con) return;

    this.con.on('message', data => {
      data.forEach(item => {

        if (item.cmd === 'Queue') {
          this.__queueInfo(item);
        }

        //用户离线
        if (item.cmd === 'UserLeave') {
          this.__userLeave(item);
        }

        //客服加入
        if (item.cmd === 'UserJoin') {
          this.__userJoin(item);
        }
      });

      // 派发消息
      this.emit('message', data);
    });
  }

  /**
   * 获取排队信息处理逻辑
   * @param data
   * @private
   */
  __queueInfo(data) {
    this.emit('Queue', data);
  }

  /**
   * 客服加入处理逻辑
   * @param data
   * @private
   */
  __userJoin(data) {
    if (data && data.head && data.head.tenantId) {
      // 代码执行到此处，data.head 必定不为空
      const { tenantId = this.tenantId, departmentId = this.departmentId } = data.head;

      //更新SDK通用参数中的tenantId
      this.updateParams({
        tenantId: this.tenantId,
        xsp_tenantid: this.tenantId,
        xsp_departmentid: this.departmentId
      });
    }

    if (data && data.head && data.head.switchMemo) {
      this.emit('UserSwitch', {
        data: data,
        type: 'join',
        message: 'New customer service joined'
      });
    } else {
      this.emit('UserJoin', {
        data: data
      });
    }
  }

  /**
   * 用户离线处理逻辑
   * @param data
   * @private
   */
  __userLeave(data) {
    let msg, type, leaveType;

    //20160427 @云田 梳理了退出逻辑
    if (data && data.head && (leaveType = data.head.leaveType)) {
      switch (leaveType) {
        case "memberForceLeave":
          msg = 'You are already logged in elsewhere!';
          type = 1;
          break;
        case "heartCheckTimeout":
          msg = 'Conversational heartbeat timeout!';
          type = 5;
          break;
        case "servicerLeave":
          msg = 'Member/Customer service proactively closes session!';
          type = 2;
          break;
        case "isSwitch":
          msg = 'You are handed over to other customer service!';
          type = 3;
          break;
        case "memberLeave":
          msg = 'Member proactively closes session!';
          type = 4;
        default:
          msg = 'Abnormal exit!';
          type = -2;
      }
    } else {
      msg = 'Abnormal exit!';
      type = -1;
    }

    if (type === 3) {
      // 代码执行到此处，data.head 必定不为空
      const { newSid = this.sid, tenantId = this.tenantId, departmentId = this.departmentId } = data.head;

      // 转交后如果 UserLeave MSG 的 head 里含有转交后生产的 sid 则替换本地的 sid
      if (this.sid !== newSid) {
        this.updateParams({ sid: newSid });
      }

      // 如果是跨 BU 转交则 UserLeave MSG 将含有转交到的 tenantId，并且如果路由或路由版本有变更则替换本地的
      if (this.tenantId !== tenantId) {
        //跨BU转交后，更新SDK通用参数中的tenantId
        this.updateParams({ tenantId: tenantId, xsp_tenantid: tenantId });
      }

      // 如果是跨 BU 转交则 UserLeave MSG 将含有转交到的 tenantId，并且如果路由或路由版本有变更则替换本地的
      if (this.departmentId !== departmentId) {
        //跨BU转交后，更新SDK通用参数中的tenantId
        this.updateParams({ departmentId: departmentId, xsp_departmentid: departmentId });
      }

      this.emit('UserSwitch', {
        data: data,
        type: 'leave',
        message: 'The current customer service exit, is being transferred to other customer service.'
      });
    } else {
      try {
        this.emit('UserLeave', {
          data: data,
          type: type,
          message: msg
        });
      } catch (e) {}

      // this.destroy()
      this.con && this.con.close();
      this.removeAllListeners();
    }
  }

  /**
   * 获取历史信息
   * @param  {Object} spec {currentPage: 1, pageSize: 20}
   * 非必传，currentPage默认1，pageSize默认20，最大50
   * @return {Promise}      接口Promise对象
   */
  getMessageLog(spec = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    return this._request("historyMessageByPage", spec);
  }

  /**
   * 获取session历史记录
   */
  getSessionLog(spec = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    spec.sid = spec.sid || this.sid;
    if (!spec.sid) {
      throw new Error('need sid as param');
    }

    return this._request("getSessionMessage", spec);
  }

  /**
   * 获取队列消息
   * @param  {Object} spec {sid: 222222}
   * @return {Promise}     接口Promise对象
   */
  getQueueInfo(spec = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    spec.skillGroupId = spec.skillGroupId || this.skillGroupId;
    return this._request('getQueueInfo', spec);
  }

  /**
   * 发生消息
   * @param  {String} type 消息类型
   * @param  {Object} body 消息内容
   * @return {Promise}     Promise
   */
  sendMessage(type, body = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    if ((type === 'img' || type === 'gif' || type === 'file') && !body.url) {
      throw new Error('Error params: you need a file url as body.url');
    }

    const params = {
      timestamp: new Date().getTime(),
      senderId: this.userId,
      senderNick: this.userName,
      msgType: '',
      shortUrl: '',
      smallUrl: '',
      shortUrl: '',
      url: '',
      fileByteSize: '',
      fileName: '',
      fileType: ''

      // 合并一下其他数据
    };parseParam(params, body);

    // 蛋疼的字段
    if (type === 'msg') {
      params.msgType = 'text';
    } else if (type === 'img' || type === 'gif') {
      params.msgType = type === 'img' ? 'normalImage' : 'liveImage';
      params.url = body.url;
      params.smallUrl = body.thumbnail;
      params.fileByteSize = body.fileSize;
    } else if (type === 'file') {
      params.msgType = 'file';
      params.url = body.url;
      params.fileName = body.fileName;
      params.fileType = body.fileType;
      params.fileByteSize = body.fileSize;
    }
    params.msgBody = body.content;
    params.shortUrl = body.shortUrl;

    return this._request('send', params);
  }

  /**
   * 获取 OSS 信息，用于上传文件
   *
   * @param {Undefined}
   *
   * @return {Promise}
   */
  _getOSSInfo() {
    return this._request('getOSSInfo').then(res => {
      const { dir, policy, accessId, signature, endpoint, cdn } = res;
      if (dir && policy && accessId && signature && endpoint) {
        return { dir, policy, accessId, signature, endpoint, cdn };
      }
      throw new Error('Get OSS info failed!');
    }, e => {
      return e;
    });
  }

  /**
   * 上传文件
   *
   * @param {File}   file     文件对象
   * @param {Object} data     表单字段对象，与 `file` 平级
   * @param {Object} options  额外的一些配置，如进度回调
   *   - options.onProgress {Function} percent => {}
   *
   * @return {Promise}
   */
  uploadFile(file, data, options) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    if (this.OSSInfo === null) {
      return this._getOSSInfo().then(res => {
        this.OSSInfo = res;
        return this._upload(file, data, options);
      }).catch(err => {
        throw err;
      });
    } else {
      return this._upload(file, data, options);
    }
  }

  _upload(file, data = {}, options = {}) {
    return new Promise((resolve, reject) => {
      const oss = this.OSSInfo;
      const xhr = new XMLHttpRequest();
      const formData = new FormData();

      for (let k in data) {
        formData.append(k, data[k]);
      }

      if (xhr.upload) {
        xhr.upload.onprogress = e => {
          if (e.total > 0) {
            e.percent = Math.round(e.loaded / e.total * 10000) / 100;
          }
          if (typeof options.onProgress === 'function') options.onProgress(e);
        };
      }

      const reg = /.*\.(\w+)$/;
      const { name = '' } = file;
      let ext, m;
      if (m = name.match(reg)) ext = m[1];
      ext = ext ? '.' + ext : '';
      const key = (oss.dir + Date.now() + '_' + uuid() + ext).replace(/^[\/\\]+/, '');

      // 设为公共读
      formData.append('x-oss-object-acl', 'public-read');

      formData.append('OSSAccessKeyId', oss.accessId);
      formData.append('policy', oss.policy);
      formData.append('Signature', oss.signature);
      formData.append('key', key);
      formData.append('file', file); // file 必须为表单中最后一个值

      let { endpoint } = oss;
      if (endpoint.indexOf('//') !== 0) {
        endpoint = '//' + endpoint;
      }

      xhr.onerror = e => {
        this.abortUpload = null;
        reject(e);
      };

      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          const err = new Error(`Uploading failed, please retry.`);
          err.status = xhr.status;
          reject(err);
        } else {
          this.abortUpload = null;
          resolve({
            url: (oss.cdn || endpoint) + '/' + key
          });
        }
      };

      xhr.open('post', endpoint);

      // 此 OSS 上传接口，CORS 配置为 `*`，因此不需要 `withCredentials`
      //xhr.withCredentials = true

      xhr.send(formData);

      this.abortUpload = xhr.abort.bind(xhr);
    });
  }

  closeSession(type = 2, reason = 10) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    let self = this;
    let _logout = function () {
      self.__userLeave({
        cmd: 'UserLeave',
        head: {
          leaveType: 'memberLeave',
          //业务上标示为主动退出
          closeReasonType: 2,
          closeReasonDetail: 10
        }
      });
    };

    //主要主动调用closeSession,不论结果如何,都强制退出云客服逻辑
    return this._request("memberLeave", {
      leaveType: type,
      leaveReason: reason
    }).then(() => {
      _logout();
    }, err => {
      _logout();
    }).catch(err => {
      _logout();
    });
  }

  createEvaluation(spec = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    return this._request('getRate', spec);
  }

  submitEvaluation(spec = {}) {
    if (!this.authenticated) {
      throw new Error('You need authenticate first!');
    }

    if (!spec.answers) {
      throw new Error('You need answers params!');
    }

    return this._request('submitRate', spec);
  }

  destroy() {
    this.con && this.con.close();

    this._resetParams();

    this.removeAllListeners();
  }

}


// WEBPACK FOOTER //
// ./node_modules/_@ali_im-sdk-third@0.1.8@@ali/im-sdk-third/lib/im.js